import { useState } from 'react';
import { api } from '../services/api';

const CONSENT_VERSION = '1.0';

interface ConsentModalProps {
  onAccept: () => void;
}

export function ConsentModal({ onAccept }: ConsentModalProps) {
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!checked) return;
    setLoading(true);
    try {
      await api.patch('/auth/me/consent', { version: CONSENT_VERSION });
    } catch {
      // non-fatal — proceed regardless so we don't block the user
    } finally {
      localStorage.setItem('ndpa_consent_version', CONSENT_VERSION);
      setLoading(false);
      onAccept();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-2xl">
        <div className="mb-4 flex items-center gap-3">
          <span className="text-2xl">🔒</span>
          <h2 className="text-xl font-bold text-gray-900">Your Privacy & Data Rights</h2>
        </div>

        <p className="mb-4 text-sm leading-relaxed text-gray-600">
          Mylighthouse is committed to protecting your personal data in accordance with Nigeria's{' '}
          <strong>Nigeria Data Protection Act, 2023 (NDPA)</strong>.
        </p>

        <div className="bg-primary-50 mb-4 space-y-2 rounded-xl p-4 text-sm text-gray-700">
          <p className="text-primary-900 font-semibold">We collect and process:</p>
          <ul className="list-inside list-disc space-y-1 text-gray-600">
            <li>Your name, email, and phone number for account management</li>
            <li>Session notes (encrypted end-to-end, therapist-only access)</li>
            <li>Assessment responses to match you with suitable therapists</li>
            <li>Payment information (processed securely via Paystack)</li>
          </ul>
        </div>

        <div className="mb-5 space-y-2 rounded-xl bg-green-50 p-4 text-sm">
          <p className="font-semibold text-green-900">Your rights under the NDPA:</p>
          <ul className="list-inside list-disc space-y-1 text-green-800">
            <li>Right to access your personal data</li>
            <li>Right to correct inaccurate data</li>
            <li>Right to erasure (&quot;right to be forgotten&quot;)</li>
            <li>Right to withdraw consent at any time</li>
            <li>Right to lodge a complaint with the Nigeria Data Protection Commission (NDPC)</li>
          </ul>
          <p className="mt-2 text-green-700">
            Contact:{' '}
            <a href="mailto:privacy@mylighthouse.ng" className="font-medium underline">
              privacy@mylighthouse.ng
            </a>
          </p>
        </div>

        <label className="mb-6 flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="text-primary-600 focus:ring-primary-500 mt-0.5 h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">
            I have read and understood how Mylighthouse processes my personal data in accordance
            with the NDPA. I consent to the processing described above.
          </span>
        </label>

        <button
          onClick={handleAccept}
          disabled={!checked || loading}
          className="bg-primary-600 hover:bg-primary-700 w-full rounded-xl py-3 font-semibold text-white transition-colors disabled:opacity-40"
        >
          {loading ? 'Saving…' : 'I Agree — Continue to Mylighthouse'}
        </button>

        <p className="mt-4 text-center text-xs text-gray-400">
          You can review our full privacy policy at mylighthouse.ng/privacy
        </p>
      </div>
    </div>
  );
}

export function useNdpaConsent() {
  const stored = localStorage.getItem('ndpa_consent_version');
  return stored === CONSENT_VERSION;
}
